import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// Booking categories (base ticket line items) eligible for a spontaneous reduction.
// Only priced categories qualify: the reduction keeps the full price due and issues a goodwill
// voucher for the value of the missing PAID tickets. A free seat (e.g. a zero-priced "Freispieler"
// / group free ticket) refunds nothing, so it must not be offered or auto-selected as the category
// to reduce — otherwise the dialog defaulted to the free category instead of the paying players.
const getBookingCategories = booking =>
  (booking?.lineItems || [])
    .filter(
      li =>
        li.type === 'booking' &&
        li.status === 'active' &&
        (li.count || 0) > 0 &&
        (Number(li.price) || 0) > 0
    )
    .map(li => ({
      categoryId: String(li.categoryId),
      name: (li.description || '').split(' - ')[0] || 'Kategorie',
      count: li.count || 0,
      price: Number(li.price) || 0,
    }));

// Dialog that explains the spontaneous reduction (full price stays due, customer gets a
// MOP-show voucher minus ticket insurance, upsells/packages of the absentees are dropped)
// and lets staff pick the category and number of absent people.
function SpontaneousReductionDialog({
  open,
  onClose,
  onConfirm,
  onPreview,
  submitting = false,
  booking,
  minimumRemainingParticipants = 0,
  initialCount = null,
}) {
  const categories = useMemo(() => getBookingCategories(booking), [booking]);
  const [categoryId, setCategoryId] = useState('');
  const totalParticipants = booking?.participantCount || 0;
  // People already recorded as absent by earlier spontaneous reductions. A reduction deliberately
  // leaves the charged participantCount and the base line items untouched (backend "Variant A":
  // the full price stays due), so both still describe the BOOKED head count — only the number of
  // people actually present drops. Every ceiling in this dialog therefore has to be measured
  // against that present count; measuring against participantCount offered seats that are already
  // gone, and the backend rejected them with MIN_ONE_PARTICIPANT_REQUIRED.
  const activeReductions = useMemo(
    () => (booking?.spontaneousReductions || []).filter(rd => rd && rd.status !== 'canceled'),
    [booking]
  );
  const alreadyReducedCount = useMemo(
    () => activeReductions.reduce((sum, rd) => sum + (Number(rd.count) || 0), 0),
    [activeReductions]
  );
  // Per-category share of those reductions, so a category cannot give up seats it already gave up.
  // Reductions without a categoryId stay unattributed and are covered by the overall cap below.
  const reducedByCategory = useMemo(() => {
    const map = new Map();
    for (const rd of activeReductions) {
      if (!rd.categoryId) continue;
      const key = String(rd.categoryId);
      map.set(key, (map.get(key) || 0) + (Number(rd.count) || 0));
    }
    return map;
  }, [activeReductions]);
  const presentParticipants = Math.max(0, totalParticipants - alreadyReducedCount);
  // Goodwill ceiling, mirrored from the backend (VOUCHER_CAP_SHARE / VOUCHER_CAP_MIN_PERSONS in
  // util/booking-handlers/spontaneous-reduction-handler.js, enforced in checkReductionFeasibility):
  // a booking may compensate at most 10% of its BOOKED participants, but never fewer than 5 people,
  // and earlier reductions count against that. Without it this dialog would keep offering counts the
  // server now rejects with SPONTANEOUS_REDUCTION_VOUCHER_CAP_EXCEEDED.
  const voucherCap = Math.max(5, Math.floor(totalParticipants * 0.1));
  const remainingVoucherCap = Math.max(0, voucherCap - alreadyReducedCount);
  // Insured bookings reach this dialog only for reductions a booked package/upsell minimum keeps
  // the refunding cancellation from covering. The smallest such reduction leaves exactly one person
  // less than that minimum; anything smaller must be cancelled with a refund instead.
  const minCount = Math.max(
    1,
    minimumRemainingParticipants > 0 ? totalParticipants - minimumRemainingParticipants + 1 : 1
  );
  // The field holds raw text so it can be emptied and retyped. Clamping on every keystroke made it
  // unclearable: deleting the "1" put the "1" straight back, the next digit appended to it ("2"
  // became "12") and the clamp pushed that to the ceiling — the count jumped to the maximum
  // instead of the 2 that was typed, and staff confirmed a reduction they never entered.
  const [countText, setCountText] = useState(String(minCount));
  // Only a plain whole number counts as an entry; "2.5" or "-1" stay invalid instead of being
  // silently reinterpreted.
  const count = /^\d+$/.test(countText.trim()) ? Number.parseInt(countText.trim(), 10) : NaN;
  // 'single' issues one voucher over the full value; 'perPerson' splits the value into one
  // voucher per reduced person so a large group can divide the compensation among its members.
  const [voucherMode, setVoucherMode] = useState('single');

  useEffect(() => {
    if (open) {
      setCategoryId(categories[0]?.categoryId || '');
      // When the dialog is opened from the reduction gate, staff already entered a count there.
      // It is still capped at the people actually present, so an earlier reduction cannot be
      // prefilled away.
      const ceiling = Math.max(minCount, Math.min(presentParticipants - 1, remainingVoucherCap));
      const prefilled = Number(initialCount);
      setCountText(
        String(
          Number.isFinite(prefilled) && prefilled >= minCount ? Math.min(prefilled, ceiling) : minCount
        )
      );
      setVoucherMode('single');
    }
  }, [open, categories, minCount, initialCount, presentParticipants, remainingVoucherCap]);

  const selectedCategory = categories.find(c => c.categoryId === categoryId) || categories[0];
  // Seats of a category that are still occupied, i.e. its booked count minus the people of that
  // category an earlier reduction already took out.
  const remainingInCategory = category =>
    category ? Math.max(0, category.count - (reducedByCategory.get(category.categoryId) || 0)) : 0;
  // At least one participant must remain PRESENT on the booking overall (mirrors the backend
  // checkReductionFeasibility, which counts participantCount minus the active reductions).
  const maxReducible = Math.max(
    0,
    Math.min(remainingInCategory(selectedCategory), presentParticipants - 1, remainingVoucherCap)
  );
  const isCountValid = Number.isInteger(count) && count >= minCount && count <= maxReducible;
  // An out-of-range entry is rejected, never silently corrected: staff keep seeing what they typed
  // and the confirm button stays disabled until the number fits.
  const countError =
    maxReducible >= minCount && countText.trim() !== '' && !isCountValid
      ? `Bitte eine Zahl zwischen ${minCount} und ${maxReducible} eingeben.`
      : null;
  // Explains the lowered ceiling in the count hint instead of just showing a smaller number.
  const reducedHint =
    alreadyReducedCount > 0
      ? ` · ${alreadyReducedCount} bereits spontan reduziert, ${presentParticipants} anwesend`
      : '';
  // Names the goodwill cap whenever IT is what limits the entry, so the lowered ceiling does not
  // look like a bug (a 12-person booking showing "Maximal 5").
  const capHint =
    remainingVoucherCap <= Math.min(remainingInCategory(selectedCategory), presentParticipants - 1)
      ? ` · Deckel: max. ${voucherCap} Personen je Buchung (10 % der Teilnehmer, mind. 5)`
      : '';
  const estimatedBase = selectedCategory ? selectedCategory.price * count : 0;

  // The MOP-show voucher is only issued once the booking is genuinely fully paid. For an unpaid
  // booking (typically an offline/cash booking whose money has not been collected yet) the reduction
  // is recorded now and the voucher is "vorgemerkt" — issued once staff mark the booking as paid.
  const isFullyPaid = !!booking?.fullyPaid;
  const hasOpenOfflinePayment = useMemo(
    () =>
      (booking?.lineItems || []).some(
        li =>
          li.status === 'active' &&
          (li.paymentReferences || []).some(r => r.paymentType === 'offline' && !r.isPaid)
      ),
    [booking]
  );

  // Exact voucher value from the backend (it deducts the product-linked ticket-insurance share,
  // which the frontend booking payload does not carry). Debounced on category/count changes; a
  // sequence guard prevents an older response from overwriting a newer one.
  const [preview, setPreview] = useState(null);
  const previewSeqRef = useRef(0);
  useEffect(() => {
    if (!open || !onPreview || !selectedCategory || !(count >= minCount) || count > maxReducible) {
      setPreview(null);
      return;
    }
    const seq = ++previewSeqRef.current;
    setPreview(prev => ({ ...(prev || {}), loading: true }));
    const handle = setTimeout(async () => {
      try {
        const res = await onPreview(selectedCategory.categoryId, count);
        if (seq !== previewSeqRef.current) return;
        setPreview({
          loading: false,
          voucherValue: typeof res?.voucherValue === 'number' ? res.voucherValue : null,
          perPersonValues: Array.isArray(res?.perPersonValues) ? res.perPersonValues : null,
          insuranceDeducted: !!res?.insuranceDeducted,
          eligible: res?.eligible !== false,
          message: res?.message || null,
        });
      } catch {
        if (seq !== previewSeqRef.current) return;
        setPreview({ loading: false, error: true });
      }
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, categoryId, count, minCount, maxReducible, selectedCategory?.categoryId]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const canConfirm =
    !submitting && !!selectedCategory && isCountValid && preview?.eligible !== false;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Spontan weniger Personen</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant='body2'>
            Es sind weniger Personen erschienen als gebucht. Die fehlenden Plätze werden als
            Gutschein erstattet — der <strong>volle Buchungspreis bleibt jedoch bestehen</strong>.
          </Typography>
          <Alert severity='info'>
            <Stack spacing={1}>
              <Typography variant='body2'>
                • Der <strong>volle Preis</strong> ist auch bei <strong>Barzahlung</strong> fällig.
              </Typography>
              <Typography variant='body2'>
                • Der Kunde erhält einen <strong>Gutschein</strong> über den Wert der stornierten
                Tickets — <strong>abzüglich der anteiligen Ticketversicherung</strong>.
              </Typography>
              <Typography variant='body2'>
                • Zugehörige <strong>Upsells/Pakete</strong> der fehlenden Personen werden storniert
                und nicht berechnet.
              </Typography>
              <Typography variant='body2'>
                • Den Gutschein <strong>nicht aktiv anbieten</strong> — erst ausstellen, wenn der
                Kunde von sich aus hinterfragt, warum der volle Preis fällig ist.
              </Typography>
              <Typography variant='body2'>
                • <strong>Ganz oder gar nicht</strong> — immer die volle Zahl der fehlenden Personen
                eintragen, nie nur einen Teil davon. Welcher Anteil erstattet wird, ist keine
                Ermessens- oder Sympathieentscheidung am Tresen.
              </Typography>
              <Typography variant='body2'>
                • Obergrenze: höchstens <strong>{voucherCap} Personen</strong> je Buchung (10 % der
                Teilnehmer, mindestens 5). Fehlen mehr, bleibt es bei diesem Maximum — ob als
                Einzelgutscheine oder als ein großer Gutschein.
              </Typography>
            </Stack>
          </Alert>

          {minimumRemainingParticipants > 0 && (
            <Alert severity='warning'>
              <Stack spacing={1}>
                <Typography variant='body2'>
                  • Diese Buchung hat eine <strong>Ticketversicherung</strong> — normalerweise wird
                  mit <strong>Erstattung storniert</strong>.
                </Typography>
                <Typography variant='body2'>
                  • Ein gebuchtes Paket/Upgrade setzt jedoch mindestens{' '}
                  <strong>{minimumRemainingParticipants} Teilnehmer</strong> voraus. Nur darunter
                  ist die Stornierung mit Erstattung nicht möglich — deshalb ab{' '}
                  <strong>{minCount} fehlenden Personen</strong> der Gutschein.
                </Typography>
                <Typography variant='body2'>
                  • Für <strong>weniger</strong> fehlende Personen bitte die reguläre
                  <strong> Teilnehmer-Stornierung</strong> mit Erstattung nutzen.
                </Typography>
              </Stack>
            </Alert>
          )}

          {!isFullyPaid && (
            <Alert severity='warning'>
              <Stack spacing={1}>
                <Typography variant='body2'>
                  • Diese Buchung ist <strong>noch nicht vollständig bezahlt</strong>
                  {hasOpenOfflinePayment ? ' (offene Vor-Ort-/Barzahlung)' : ''}.
                </Typography>
                <Typography variant='body2'>
                  • Der Gutschein wird <strong>vorgemerkt</strong> und erst ausgestellt, sobald die
                  Buchung als <strong>vollständig bezahlt</strong> markiert wurde.
                </Typography>
              </Stack>
            </Alert>
          )}

          {categories.length > 1 && (
            <FormControl fullWidth size='small'>
              <InputLabel id='spontaneous-reduction-category-label'>Kategorie</InputLabel>
              <Select
                labelId='spontaneous-reduction-category-label'
                label='Kategorie'
                value={categoryId}
                onChange={e => setCategoryId(e.target.value)}
              >
                {categories.map(c => (
                  <MenuItem key={c.categoryId} value={c.categoryId}>
                    {c.name} ({remainingInCategory(c)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            type='number'
            size='small'
            label='Anzahl fehlender Personen'
            value={countText}
            // Focusing selects the value so typing replaces it instead of appending to it.
            onFocus={e => e.target.select()}
            onChange={e => setCountText(e.target.value)}
            inputProps={{ min: minCount, max: maxReducible }}
            error={!!countError}
            helperText={
              countError ||
              (maxReducible < minCount
                ? remainingVoucherCap < minCount
                  ? `Gutschein-Deckel ausgeschöpft: max. ${voucherCap} Personen je Buchung, davon ${alreadyReducedCount} bereits erstattet`
                  : `Reduktion nicht möglich (mind. 1 Teilnehmer)${reducedHint}`
                : minCount > 1
                  ? `Von ${minCount} bis ${maxReducible} (darunter bitte regulär mit Erstattung stornieren)${reducedHint}${capHint}`
                  : `Maximal ${maxReducible} (mind. 1 Teilnehmer verbleibt)${reducedHint}${capHint}`)
            }
          />

          {count > 1 && (
            <FormControl>
              <FormLabel id='spontaneous-reduction-voucher-mode-label' sx={{ fontSize: 14 }}>
                Gutschein-Ausstellung
              </FormLabel>
              <RadioGroup
                aria-labelledby='spontaneous-reduction-voucher-mode-label'
                value={voucherMode}
                onChange={e => setVoucherMode(e.target.value)}
              >
                <FormControlLabel
                  value='single'
                  control={<Radio size='small' />}
                  label={
                    <Typography variant='body2'>
                      <strong>Ein Gutschein</strong> über den Gesamtwert
                    </Typography>
                  }
                />
                <FormControlLabel
                  value='perPerson'
                  control={<Radio size='small' />}
                  label={
                    <Typography variant='body2'>
                      <strong>{count} Gutscheine</strong> — ein Gutschein pro fehlender Person
                      (Wert aufgeteilt, z.&nbsp;B. für große Gruppen)
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
          )}

          {selectedCategory && count >= minCount && (
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              {preview?.loading
                ? 'Gutscheinwert wird berechnet …'
                : preview && typeof preview.voucherValue === 'number'
                  ? `Gutscheinwert: ${preview.voucherValue.toFixed(2)} €${
                      voucherMode === 'perPerson' && count > 1
                        ? ` gesamt, aufgeteilt auf ${count} Gutscheine (${
                            preview.perPersonValues && preview.perPersonValues.length > 0
                              ? `je ca. ${preview.perPersonValues[
                                  preview.perPersonValues.length - 1
                                ].toFixed(2)} €`
                              : `je ca. ${(preview.voucherValue / count).toFixed(2)} €`
                          })`
                        : ''
                    }${
                      preview.insuranceDeducted ? ' (abzüglich anteiliger Ticketversicherung)' : ''
                    }`
                  : `Voraussichtlicher Gutscheinwert: ca. ${estimatedBase.toFixed(2)} € (abzüglich Ticketversicherung; der exakte Betrag wird berechnet).`}
              {!isFullyPaid && ' Ausstellung erst nach vollständiger Bezahlung.'}
            </Typography>
          )}

          {preview && preview.eligible === false && preview.message && (
            <Alert severity='error'>{preview.message}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          color='warning'
          onClick={() => onConfirm(count, selectedCategory?.categoryId, voucherMode)}
          disabled={!canConfirm}
        >
          {submitting ? (
            <CircularProgress size={18} color='inherit' />
          ) : isFullyPaid ? (
            'Reduzieren & Gutschein'
          ) : (
            'Reduzieren & Gutschein vormerken'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SpontaneousReductionDialog;
